import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Paperclip, X, Image as ImageIcon } from 'lucide-react';
import { useChatStore } from '../stores/chatStore';
import { useI18n } from '../i18n';

interface ChatInputProps {
    externalFiles?: File[];
    onExternalFilesConsumed?: () => void;
}

export default function ChatInput({ externalFiles, onExternalFilesConsumed }: ChatInputProps) {
    const [message, setMessage] = useState('');
    const [attachments, setAttachments] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const { t } = useI18n();

    const { sendMessage, isStreaming } = useChatStore();

    // Handle external files from Chat component drag-drop
    useEffect(() => {
        if (externalFiles && externalFiles.length > 0) {
            setAttachments(prev => [...prev, ...externalFiles].slice(0, 5));
            onExternalFilesConsumed?.();
        }
    }, [externalFiles, onExternalFilesConsumed]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
        }
    }, [message]);

    // Handle submit
    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();

        if ((!message.trim() && attachments.length === 0) || isStreaming) return;

        const currentMessage = message;
        const currentAttachments = attachments;

        setMessage('');
        setAttachments([]);

        await sendMessage(currentMessage, currentAttachments, (newChatId) => {
            // Navigate to the new chat when created
            navigate(`/chat/${newChatId}`);
        });
    };

    // Handle keyboard
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    // Handle file selection
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        addFiles(files);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle drag and drop
    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragIn = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragOut = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        addFiles(files);
    };

    // Add files to attachments
    const addFiles = (files: File[]) => {
        const validFiles = files.filter(
            (file) => file.type.startsWith('image/') || file.type.startsWith('video/')
        );
        setAttachments((prev) => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
    };

    // Remove attachment
    const removeAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="border-t border-dark-800 bg-dark-900/50">
            {/* Attachments preview */}
            {attachments.length > 0 && (
                <div className="px-4 pt-3 flex gap-2 flex-wrap">
                    {attachments.map((file, index) => (
                        <div
                            key={index}
                            className="relative group w-20 h-20 rounded-lg overflow-hidden border border-dark-700"
                        >
                            {file.type.startsWith('image/') ? (
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full bg-dark-800 flex items-center justify-center">
                                    <ImageIcon className="w-6 h-6 text-dark-500" />
                                </div>
                            )}
                            <button
                                onClick={() => removeAttachment(index)}
                                className="absolute top-1 right-1 p-1 bg-dark-900/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <X className="w-3 h-3 text-dark-300" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Input area */}
            <form
                onSubmit={handleSubmit}
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`p-4 transition-colors ${isDragging ? 'bg-accent-500/10' : ''}`}
            >
                <div className="flex items-end gap-3">
                    {/* Attachment button */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isStreaming}
                        className="btn-icon mb-1"
                        title={t.gallery.upload}
                    >
                        <Paperclip className="w-5 h-5" />
                    </button>

                    {/* Textarea */}
                    <div className="flex-1 relative">
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={isDragging ? t.gallery.upload : t.chat.typeMessage}
                            disabled={isStreaming}
                            rows={1}
                            className="input resize-none py-3 pr-12 max-h-[200px]"
                        />
                    </div>

                    {/* Send button */}
                    <button
                        type="submit"
                        disabled={(!message.trim() && attachments.length === 0) || isStreaming}
                        className="btn-primary px-4 py-3"
                    >
                        {isStreaming ? (
                            <div className="spinner w-5 h-5" />
                        ) : (
                            <Send className="w-5 h-5" />
                        )}
                    </button>
                </div>

                {/* Hint */}
                <p className="text-xs text-dark-500 mt-2 text-center">
                    Enter로 전송, Shift+Enter로 줄바꿈
                </p>
            </form>
        </div>
    );
}
